/**
 * SlashCommandMenu Component
 * Popup menu for inserting entities via slash commands
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { SLASH_COMMANDS, type SlashCommand } from '@/types/document'

interface SlashCommandMenuProps {
  isOpen: boolean
  position: { top: number; left: number }
  filter: string
  onSelect: (command: SlashCommand) => void
  onClose: () => void
}

export function SlashCommandMenu({
  isOpen,
  position,
  filter,
  onSelect,
  onClose,
}: SlashCommandMenuProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const menuRef = useRef<HTMLDivElement>(null)

  // Filter commands based on input
  const filteredCommands = SLASH_COMMANDS.filter((cmd) =>
    cmd.name.toLowerCase().includes(filter.toLowerCase()) ||
    cmd.shortcut.toLowerCase().includes(filter.toLowerCase())
  )

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0)
  }, [filter])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) =>
            prev < filteredCommands.length - 1 ? prev + 1 : 0
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredCommands.length - 1
          )
          break
        case 'Enter':
          e.preventDefault()
          if (filteredCommands[selectedIndex]) {
            onSelect(filteredCommands[selectedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, filteredCommands, selectedIndex, onSelect, onClose])

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  if (!isOpen || filteredCommands.length === 0) {
    return null
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white rounded-lg border border-warm-200 shadow-lg py-2 min-w-[240px] max-h-[320px] overflow-y-auto"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <div className="px-3 py-1.5 text-[10px] font-semibold text-text-muted uppercase tracking-wide">
        Insert Entity
      </div>

      {filteredCommands.map((command, index) => (
        <button
          key={command.id}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors',
            index === selectedIndex
              ? 'bg-warm-100'
              : 'hover:bg-warm-50'
          )}
          onClick={() => onSelect(command)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div
            className="w-8 h-8 rounded-md flex items-center justify-center text-sm font-medium"
            style={{
              backgroundColor: `${command.color}15`,
              color: command.color,
            }}
          >
            {command.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-text-primary">
              {command.name}
            </div>
            <div className="text-xs text-text-muted truncate">
              {command.description}
            </div>
          </div>
          <span className="text-[10px] text-text-muted font-mono">
            {command.shortcut}
          </span>
        </button>
      ))}

      <div className="px-3 pt-2 mt-1 border-t border-warm-100">
        <div className="flex items-center gap-2 text-[10px] text-text-muted">
          <span className="px-1.5 py-0.5 bg-warm-100 rounded text-[10px] font-mono">↑↓</span>
          <span>navigate</span>
          <span className="px-1.5 py-0.5 bg-warm-100 rounded text-[10px] font-mono">↵</span>
          <span>select</span>
          <span className="px-1.5 py-0.5 bg-warm-100 rounded text-[10px] font-mono">esc</span>
          <span>close</span>
        </div>
      </div>
    </div>
  )
}

// Hook for managing slash command state
export function useSlashCommand() {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [filter, setFilter] = useState('')
  const [startPos, setStartPos] = useState<number | null>(null)

  const open = useCallback((cursorPosition: { top: number; left: number }, editorPos: number) => {
    setPosition({
      top: cursorPosition.top + 24,
      left: cursorPosition.left,
    })
    setStartPos(editorPos)
    setFilter('')
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    setFilter('')
    setStartPos(null)
  }, [])

  const updateFilter = useCallback((text: string) => {
    setFilter(text)
  }, [])

  return {
    isOpen,
    position,
    filter,
    startPos,
    open,
    close,
    updateFilter,
  }
}
